// src/SetupWizard/Step3Frequency.js
import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  ToggleButton, 
  ToggleButtonGroup,
  Tooltip,
  Divider,
  Chip,
  Alert
} from '@mui/material';
import {
  Today as TodayIcon,
  Schedule as ScheduleIcon,
  CalendarMonth as CalendarIcon
} from '@mui/icons-material';

function Step3Frequency({ frequency, onChange }) {
  const [selectedDays, setSelectedDays] = useState(frequency === 'all' ? [] : (Array.isArray(frequency) ? frequency : []));
  const [frequencyType, setFrequencyType] = useState(frequency === 'all' ? 'daily' : (Array.isArray(frequency) && frequency.length === 5 ? 'weekdays' : 'custom'));
  const [error, setError] = useState('');

  useEffect(() => {
    validateAndUpdate(frequencyType, selectedDays);
  }, []);

  const validateAndUpdate = (type, days) => {
    if (type === 'custom' && days.length === 0) {
      setError('Please select at least one day for custom schedule');
      onChange({ frequency: days, isValid: false });
    } else {
      setError('');
      onChange({ 
        frequency: type === 'daily' ? 'all' : days,
        isValid: true 
      });
    }
  };

  const handleFrequencyTypeChange = (event, newFrequency) => {
    if (!newFrequency) return; // Prevent deselection
    
    setFrequencyType(newFrequency);
    
    switch (newFrequency) {
      case 'daily':
        setSelectedDays([]);
        setError('');
        onChange({ frequency: 'all', isValid: true });
        break;
      case 'weekdays':
        const weekdays = daysOfWeek.slice(0, 5).map(d => d.day);
        setSelectedDays(weekdays);
        setError('');
        onChange({ frequency: weekdays, isValid: true });
        break;
      case 'custom':
        setSelectedDays([]);
        setError('Please select at least one day for custom schedule');
        onChange({ frequency: [], isValid: false });
        break;
      default:
        break;
    }
  };

  const handleDayToggle = (day) => {
    let newSelectedDays;
    if (selectedDays.includes(day)) {
      newSelectedDays = selectedDays.filter(d => d !== day);
    } else {
      newSelectedDays = [...selectedDays, day];
    }
    setSelectedDays(newSelectedDays);
    validateAndUpdate('custom', newSelectedDays);
  };

  const daysOfWeek = [
    { day: 'Monday', short: 'Mon' },
    { day: 'Tuesday', short: 'Tue' },
    { day: 'Wednesday', short: 'Wed' },
    { day: 'Thursday', short: 'Thu' },
    { day: 'Friday', short: 'Fri' },
    { day: 'Saturday', short: 'Sat' },
    { day: 'Sunday', short: 'Sun' }
  ];

  const frequencyOptions = [
    { value: 'daily', label: 'Daily', icon: <TodayIcon />, tooltip: 'Post every day' },
    { value: 'weekdays', label: 'Weekdays', icon: <ScheduleIcon />, tooltip: 'Post Monday to Friday' },
    { value: 'custom', label: 'Custom', icon: <CalendarIcon />, tooltip: 'Select specific days' }
  ];

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Step 3: Set Your Posting Schedule
      </Typography>
      
      <Typography variant="body1" sx={{ mb: 3 }}>
        Choose how often you want to publish new content to maintain consistent engagement with your audience.
      </Typography>

      {/* Frequency Type Selection */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Select Posting Frequency:
        </Typography>
        <ToggleButtonGroup
          value={frequencyType}
          exclusive
          onChange={handleFrequencyTypeChange}
          aria-label="posting frequency"
          fullWidth
          sx={{ mb: 2 }}
        >
          {frequencyOptions.map((option) => (
            <ToggleButton 
              key={option.value} 
              value={option.value}
              aria-label={option.label}
            >
              <Tooltip title={option.tooltip}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {option.icon}
                  <span>{option.label}</span>
                </Box>
              </Tooltip>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {/* Selected Schedule Display */}
        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Current Schedule:
          </Typography>
          <Typography variant="body1">
            {frequencyType === 'daily' && 'Publishing every day'}
            {frequencyType === 'weekdays' && 'Publishing Monday through Friday'}
            {frequencyType === 'custom' && (selectedDays.length === 0 
              ? 'No days selected' 
              : `Publishing on ${selectedDays.join(', ')}`)}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>

      {/* Custom Day Selection */}
      {frequencyType === 'custom' && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Select Publishing Days:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
            {daysOfWeek.map(({ day, short }) => (
              <Chip
                key={day}
                label={window.innerWidth > 600 ? day : short}
                onClick={() => handleDayToggle(day)}
                color={selectedDays.includes(day) ? "primary" : "default"}
                variant={selectedDays.includes(day) ? "filled" : "outlined"}
                sx={{ 
                  minWidth: '80px',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'scale(1.05)'
                  }
                }}
              />
            ))}
          </Box>
        </Paper>
      )}

      {/* Tips Section */}
      <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
        <Typography variant="subtitle2" color="text.secondary">
          💡 Tips for posting frequency:
        </Typography>
        <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
          <li>Daily posts are great for news and trending topics</li>
          <li>Weekday posts work well for professional or business content</li>
          <li>Custom schedules help balance quality and consistency</li>
          <li>Consider your content creation capacity when choosing frequency</li>
        </ul>
      </Paper>
    </Box>
  );
}

export default Step3Frequency;
